import React, { useState, useEffect } from 'react';
import { FiChevronLeft } from "react-icons/fi";
import { FaBook } from "react-icons/fa";
import styled from 'styled-components';

// Icono hamburguesa circular (tres líneas verticales)
const HamburgerCircle = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #0151a2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HamburgerLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  justify-content: center;
  > span {
    display: block;
    width: 5px;
    height: 17px;
    border-radius: 3px;
    background: #fff;
    transition: background 0.2s;
  }
`;

const SidebarContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: ${(props) => (props.isCollapsed ? '80px' : '260px')};
  background: #02396d;
  color: #fff;
  transition: width 0.3s, background 0.3s;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  overflow-y: auto;
  border-radius: ${(props) => (props.isCollapsed ? '0 0 16px 0' : '0')};
  box-shadow: 0 8px 24px 0 rgba(40,80,120,0.10);
  /* Sin top ni margin-top */
`;

const SidebarToggle = styled.div`
  position: absolute;
  right: -20px;
  top: 20px;
  background: transparent;
  color: #fff;
  cursor: pointer;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1010;
  border: none;
  outline: none;
`;

const SidebarHeader = styled.div`
  text-align: center;
  padding: 22px 8px 18px 8px;
  border-bottom: 1px solid #0151a2;
`;

const SidebarLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  h3 {
    color: #fff;
    font-weight: bold;
    font-size: 22px;
    margin: 0;
    transition: color 0.3s;
    letter-spacing: 1.2px;
  }
`;

const SidebarMenu = styled.nav`
  flex-grow: 1;
  padding: 12px 4px;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
`;

const MenuItem = styled.li`
  margin-bottom: 4px;
  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: #fff;
    padding: 10px 18px;
    font-size: 16px;
    border-radius: 8px;
    font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
    background: ${(props) => (props.active ? '#0151a2' : 'transparent')};
    transition: background 0.2s, color 0.2s;
    box-shadow: ${(props) => (props.active ? '0 2px 12px 0 rgba(1,81,162,0.10)' : 'none')};
    &:hover {
      background: #0151a2;
      color: #fff;
    }
  }
`;

const Pagination = styled.div`
  text-align: center;
  padding: 10px;
  font-size: 14px;
  border-top: 1px solid #0151a2;
  background: #02396d;
  color: #fff;
  button {
    background: transparent;
    border: none;
    color: #fff;
    font-size: 16px;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 4px;
    &:hover:enabled {
      background: #0151a2;
    }
    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }
`;

// ------------------- COMPONENTE -------------------
const Sidebar = ({ days, selectedDay, onSelect, onToggle }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 7;

  const totalPages = Math.ceil(days.length / itemsPerPage);
  const visibleItems = days.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    function checkMobile() {
      setIsCollapsed(window.innerWidth <= 768);
    }
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleToggle = () => {
    setIsCollapsed((prev) => !prev);
    if (onToggle) onToggle();
  };

  return (
    <SidebarContainer isCollapsed={isCollapsed}>
      <SidebarToggle onClick={handleToggle} aria-label="Toggle sidebar">
        {isCollapsed ? (
          <HamburgerCircle>
            <HamburgerLines>
              <span />
              <span />
              <span />
            </HamburgerLines>
          </HamburgerCircle>
        ) : (
          <FiChevronLeft size={30} color="#fff" />
        )}
      </SidebarToggle>

      <SidebarHeader>
        <SidebarLogo>
          <FaBook size={26} color="#fff" />
          {!isCollapsed && <h3>CDB</h3>}
        </SidebarLogo>
      </SidebarHeader>

      <SidebarMenu>
        <ul>
          {visibleItems.map((day, index) => (
            <MenuItem
              key={index}
              active={selectedDay === day}
            >
              <a href="#" onClick={() => onSelect(day)}>
                {isCollapsed
                  ? index + 1 + (currentPage - 1) * itemsPerPage
                  : `Día ${index + 1 + (currentPage - 1) * itemsPerPage}`}
              </a>
            </MenuItem>
          ))}
        </ul>
      </SidebarMenu>

      {totalPages > 1 && (
        <Pagination>
          <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
            &lt;
          </button>
          &nbsp; {currentPage} - {totalPages} &nbsp;
          <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
            &gt;
          </button>
        </Pagination>
      )}
    </SidebarContainer>
  );
};

export default Sidebar;
